import React, { useMemo, useState } from "react";
import { CircularProgress, circularProgressClasses } from "@mui/material";

import { ButtonContainer, Link, FocusedBox } from "./styles";

/**
 * Componente de botão personalizado.
 * @param {object} props As propriedades do botão.
 * @param {React.ReactNode} props.children O conteúdo do botão.
 * @param {string} [props.link] O link para redirecionar ao clicar no botão.
 * @param {string} [props.title] O título do botão.
 * @param {string} [props.type="button"] O tipo do botão.
 * @param {boolean} [props.disabled=false] Indica se o botão está desativado.
 * @param {function} [props.onClick] A função a ser executada ao clicar no botão.
 * @param {string|boolean} [props.variant=false] O estilo do botão.
 * @param {boolean} [props.large=false] Indica se o botão é grande.
 * @param {boolean} [props.loading=false] Indica se o botão está em um estado de carregamento.
 * @param {boolean} [props.shadowy] Indica se o botão tem sombra.
 * @param {boolean} [props.noLink=false] Indica se o botão não é um link.
 * @param {string} [props.maxWidth] A largura máxima do botão.
 * @param {string} [props.linkWidth] A largura do link.
 * @param {boolean} [props.isVariant=false] Indica se o botão tem variação.
 * @param {string} [props.labelAccessibility] O rótulo de acessibilidade do botão.
 * @returns {React.ReactNode} O componente do botão.
 */
function Button({
  children,
  link,
  title,
  type = "button",
  disabled = false,
  onClick,
  variant = false,
  large = false,
  loading = false,
  shadowy,
  noLink = false,
  maxWidth,
  linkWidth,
  isVariant = false,
  labelAccessibility = "",
  error
}) {

  /**
 * Retorna o componente Wrapper com base nas propriedades fornecidas.
 * @returns {React.ReactNode} O componente Wrapper.
  */
  const Wrapper = useMemo(
    () =>
      noLink
        ? ({ children: child }) => child
        : ({ children: child }) => (
            <Link href={link} linkWidth={linkWidth} onClick={onClick}>
              {child}
            </Link>
          ),
    [noLink, link, onClick]
    );

  const [focused, setFocused] = useState(false);

  return (
    <FocusedBox focused={focused} maxWidth={maxWidth}>
      <Wrapper>
        <ButtonContainer
          disabled={loading || disabled}
          type={type}
          className={[variant, disabled && "disabled"].join(" ")}
          large={large}
          shadowy={shadowy}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onClick={noLink ? onClick : undefined}
          maxWidth={maxWidth}
          isVariant={isVariant}
          aria-label={labelAccessibility}
        >
          {loading ? (
            <CircularProgress
              size={20}
              color="inherit"
              thickness={4}
              sx={{
                [circularProgressClasses.circle]: {
                  fill: "#fff !important",
                  color: "#fff !important",
                },
              }}
            />
          ) : (
            title || children
          )}
        </ButtonContainer>
      </Wrapper>
    </FocusedBox>
  );
}

export default Button;
